import {
  Button,
  Flex,
  Input,
  Modal,
  NumberInput,
  ScrollArea,
  TextInput,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useCallback } from "react";

import {
  FileResult,
  UpdateWaiveInput,
  UpdateWaiveMutationVariables,
} from "../../../graphql/generated";
import { Waive } from "../../../types/waive";
import {
  formatToNumber,
  parseStringToNumber,
  priceFormatter,
} from "../../../utils/number.ts";
import Dropzone from "../../Dropzone/Dropzone";

interface Props {
  opened: boolean;
  value: Waive;
  onClose: () => void;
  onSubmit: (variables: UpdateWaiveMutationVariables) => void;
}

export default function WaiveEditModal({
  opened = false,
  value,
  onClose,
  onSubmit,
}: Props) {
  const form = useForm<Partial<Waive>>({
    initialValues: {
      amount: formatToNumber(value.amount),
      requestDocument: value.requestDocument,
    },
    validate: {
      amount: isNotEmpty("Field is required"),
      requestDocument: isNotEmpty("Field is required"),
    },
    validateInputOnBlur: true,
  });

  const handleClose = useCallback(() => {
    onClose();
    form.reset();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    onSubmit({
      id: value.id,
      input: {
        ...form.values,
        requestDocument: form.values.requestDocument?.filePath,
      } as UpdateWaiveInput,
    });
    handleClose();
  }, [onSubmit, value.id, form.values, handleClose]);

  const handleFileUploaded = (files: FileResult[]) => {
    form.setValues({ requestDocument: files[0] });
  };

  const handleRemove = () => {
    form.setValues({ requestDocument: undefined });
  };

  return (
    <>
      <Modal
        title="Edit Waive Request"
        opened={opened}
        scrollAreaComponent={ScrollArea.Autosize}
        closeButtonProps={{
          size: 24,
          iconSize: 24,
        }}
        onClose={handleClose}
      >
        <Flex direction="column" gap={20}>
          <TextInput
            size="m"
            disabled
            label="Invoice Number"
            placeholder="Select Invoice Number"
            value={value.invoice.invoiceNumber}
          />

          <NumberInput
            required
            precision={2}
            parser={parseStringToNumber}
            formatter={priceFormatter}
            hideControls
            label="Waive Amount"
            placeholder="Enter Waive Amount"
            size="m"
            {...form.getInputProps("amount")}
          />

          <Flex direction="column" gap={2}>
            <Input.Label required>Request Document</Input.Label>
            <Dropzone
              maxFiles={1}
              value={
                form.values.requestDocument
                  ? [form.values.requestDocument]
                  : null
              }
              onUploadSuccess={handleFileUploaded}
              onRemove={handleRemove}
            />
          </Flex>

          <Flex gap={20} justify="end">
            <Button
              variant="outlineBlue"
              uppercase
              type="reset"
              onClick={handleClose}
            >
              cancel
            </Button>
            <Button
              variant="filledBlue"
              uppercase
              type="submit"
              disabled={!form.isValid()}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
